import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PremiumCard } from "../components/PremiumCard";
import { PremiumButton } from "../components/PremiumButton";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { fadeIn, slideUp, staggerContainer } from "../animations";
import * as Icons from "lucide-react";

export const C2BVorteile = () => {
  const [refVorteile, isVorteileVisible] = useScrollReveal({ once: true });
  const [refNachhaltigkeit, isNachhaltigkeitVisible] = useScrollReveal({ once: true });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1f7db6] via-[#143e6e] to-[#1f7db6]" />
        
        <motion.div
          className="absolute top-20 -left-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-20 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />

        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.h1 variants={slideUp} className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
            Ihre Vorteile
          </motion.h1>
          <motion.p variants={slideUp} className="text-xl sm:text-2xl text-white/90 mb-10 max-w-3xl mx-auto">
            Warum Tausende Kunden uns vertrauen
          </motion.p>
          <motion.div variants={fadeIn}>
            <Link to="/">
              <PremiumButton variant="secondary" size="lg" icon={Icons.ArrowRight}>
                Jetzt verkaufen
              </PremiumButton>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Vorteile */}
      <section ref={refVorteile} className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            animate={isVorteileVisible ? "visible" : "hidden"}
            variants={slideUp}
            className="text-4xl sm:text-5xl font-bold text-[#143e6e] text-center mb-16"
          >
            Das macht uns besonders
          </motion.h2>
          
          <motion.div
            initial="hidden"
            animate={isVorteileVisible ? "visible" : "hidden"}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {[
              { icon: Icons.Zap, title: "Sofortangebot", desc: "Faire Preise in Echtzeit - keine versteckten Kosten", color: "from-yellow-500 to-orange-500" },
              { icon: Icons.Shield, title: "Sicher", desc: "Zertifizierte Datenlöschung & versicherter Versand", color: "from-green-500 to-emerald-600" },
              { icon: Icons.Euro, title: "Beste Preise", desc: "Marktführende Ankaufspreise für Ihre Geräte", color: "from-[#1f7db6] to-[#143e6e]" },
              { icon: Icons.Clock, title: "Schnell", desc: "Auszahlung innerhalb von 1-2 Werktagen", color: "from-blue-500 to-indigo-600" },
              { icon: Icons.Leaf, title: "Nachhaltig", desc: "Second Life statt Müll - gut für die Umwelt", color: "from-green-600 to-teal-600" },
              { icon: Icons.Heart, title: "Einfach", desc: "Unkomplizierter Prozess ohne Papierkram", color: "from-pink-500 to-rose-600" }
            ].map((item, idx) => (
              <motion.div key={idx} variants={slideUp}>
                <PremiumCard className="p-8 text-center h-full">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className={`w-16 h-16 mx-auto mb-6 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center`}
                  >
                    <item.icon className="text-white" size={32} />
                  </motion.div>
                  <h3 className="font-bold text-xl mb-3 text-[#143e6e]">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </PremiumCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Nachhaltigkeit */}
      <section ref={refNachhaltigkeit} className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            animate={isNachhaltigkeitVisible ? "visible" : "hidden"}
            variants={slideUp}
            className="text-4xl sm:text-5xl font-bold text-[#143e6e] text-center mb-16"
          >
            Gut für Sie & die Umwelt
          </motion.h2>

          <motion.div
            initial="hidden"
            animate={isNachhaltigkeitVisible ? "visible" : "hidden"}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          >
            {[
              { icon: Icons.Recycle, stat: "10.000+", label: "Geräte wiederverwertet" },
              { icon: Icons.TreePine, stat: "50 Tonnen", label: "CO₂ eingespart" },
              { icon: Icons.Users, stat: "5.000+", label: "Zufriedene Kunden" }
            ].map((item, idx) => (
              <motion.div key={idx} variants={slideUp}>
                <PremiumCard className="p-8 text-center">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center"
                  >
                    <item.icon className="text-white" size={32} />
                  </motion.div>
                  <div className="text-5xl font-bold bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent mb-3">
                    {item.stat}
                  </div>
                  <p className="text-gray-700 font-semibold">{item.label}</p>
                </PremiumCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1f7db6] via-[#143e6e] to-[#1f7db6]" />
        
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.h2 variants={slideUp} className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Überzeugt?
          </motion.h2>
          <motion.p variants={slideUp} className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Verkaufen Sie jetzt Ihr Gerät und profitieren Sie von unseren Vorteilen
          </motion.p>
          <motion.div variants={fadeIn}>
            <Link to="/">
              <PremiumButton variant="secondary" size="lg" icon={Icons.ArrowRight}>
                Jetzt Angebot erhalten
              </PremiumButton>
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};
